/**
 * ─────────────────────────────────────────────────────────────────────────────
 * @module  public/js/services/contactsStagingPhoneFinder.js
 * @summary Reads staged contact phone numbers directly from Firestore by doc ID.
 *
 * @description
 * • Reads `/ContactsStaging/{contactId}` with a tenant-scoped DocumentReference
 * • Returns the raw staged phone fields for downstream formatting/rendering
 * • Falls back to collection queries when the direct document lookup is empty
 *
 * ## Public API
 * async lookupStagedPhone(contactId: string): Promise<string>
 *
 * @author   TopSail AI
 * @version  1.0.1
 * @since    2025-05-15
 * ─────────────────────────────────────────────────────────────────────────────
 */

import {
  getDocFromServer,
  getDocs,
  query,
  where,
  limit,
} from 'https://www.gstatic.com/firebasejs/9.17.2/firebase-firestore.js';
import { colWithTenant, docWithTenant } from '../utils/firestoreHelpers.js';
import { resolveRuntimeDi } from '../utils/runtimeDi.js';

/**
 * Fetches phone numbers from the ContactStaging Firestore collection
 * by reading the staged contact document directly.
 * @param {string} contactId - The ContactsStaging document ID to look up.
 * @param {Record<string, unknown>} [services] - Optional DI overrides.
 * @returns {Promise<object>} - Phone fields keyed by writer field names.
 */
export async function lookupStagedPhones(contactId, services = null) {
  if (!contactId) return {};
  try {
    const di = resolveRuntimeDi(services);
    const user = di.auth?.currentUser ?? (await di.auth?.waitForLogin?.());
    const tenantId = user?.tenantId;
    if (!tenantId) {
      console.error('Missing tenant ID');
      throw new Error('Missing tenant ID');
    }
    const ref = docWithTenant(tenantId, `ContactsStaging/${contactId}`);
    const snap = await getDocFromServer(ref);
    if (snap.exists()) {
      const data = snap.data() || {};
      return {
        mobilePhone: data.mobilePhone ?? '',
        mobilePhoneExtension: data.mobilePhoneExtension ?? '',
        officePhone: data.officePhone ?? '',
        officePhoneExtension: data.officePhoneExtension ?? '',
        mainOfficePhone: data.mainOfficePhone ?? '',
        mainOfficePhoneExtension: data.mainOfficePhoneExtension ?? '',
        phone: data.phone ?? '',
        phoneExtension: data.phoneExtension ?? '',
      };
    }

    const col = colWithTenant(tenantId, 'ContactsStaging');
    const fallbackQueries = [
      query(col, where('crmContactId', '==', contactId), limit(1)),
      query(col, where('contactId', '==', contactId), limit(1)),
    ];

    for (const q of fallbackQueries) {
      const fallbackSnap = await getDocs(q);
      if (!fallbackSnap.empty) {
        const data = fallbackSnap.docs[0].data() || {};
        return {
          mobilePhone: data.mobilePhone ?? '',
          mobilePhoneExtension: data.mobilePhoneExtension ?? '',
          officePhone: data.officePhone ?? '',
          officePhoneExtension: data.officePhoneExtension ?? '',
          mainOfficePhone: data.mainOfficePhone ?? '',
          mainOfficePhoneExtension: data.mainOfficePhoneExtension ?? '',
          phone: data.phone ?? '',
          phoneExtension: data.phoneExtension ?? '',
        };
      }
    }

    return {};
  } catch (err) {
    console.error('[contactsStagingPhoneFinder] lookup failed:', err);
    return {};
  }
}

/**
 * Fetch the first available staged phone number for a contact.
 *
 * @param {string} contactId - The ContactsStaging document or CRM contact ID.
 * @param {Record<string, unknown>} [services] - Optional DI overrides.
 * @returns {Promise<string>} First phone number found, or an empty string.
 */
export async function lookupStagedPhone(contactId, services = null) {
  const all = await lookupStagedPhones(contactId, services);
  return (
    all.mobilePhone || all.officePhone || all.mainOfficePhone || all.phone || ''
  );
}
